/** Mois saisis : CA HT et TVA, année en cours et année d’avant. */
import { useEffect, useState } from "react";
import { facturxFetch } from "../../api/client";
import { Button } from "../../components/Button";
import { useCompany } from "../../company/CompanyProvider";
import { centsToInput, inputToCents, typedDecimal } from "../catalog/types";
import { formatsFromCompany } from "../../formats/types";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

type OpeningRow = { year: number; month: number; ca_ht_cents: number; vat_cents: number | null };

type Cell = { ca: string; vat: string };

function blankYear(): Cell[] {
  return MONTHS.map(() => ({ ca: "", vat: "" }));
}

function readCell(raw: string, decimalComma: boolean): number | null | "bad" {
  if (raw.trim() === "") return null;
  const cents = inputToCents(raw, decimalComma);
  return cents === null ? "bad" : cents;
}

export function PriorTurnover() {
  const { company } = useCompany();
  const decimalComma = formatsFromCompany(company).decimal_comma;
  const [year, setYear] = useState(new Date().getFullYear());
  const [prevYear, setPrevYear] = useState(year - 1);
  const [current, setCurrent] = useState<Cell[]>(blankYear);
  const [previous, setPrevious] = useState<Cell[]>(blankYear);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!company) return;
    let stop = false;
    facturxFetch<{ year: number; prev_year: number; rows: OpeningRow[] }>(`realized-import.php?company_id=${company.id}`)
      .then((data) => {
        if (stop) return;
        setYear(data.year);
        setPrevYear(data.prev_year);
        const now = blankYear();
        const before = blankYear();
        for (const row of data.rows || []) {
          const slot = row.year === data.year ? now : row.year === data.prev_year ? before : null;
          if (!slot || row.month < 1 || row.month > 12) continue;
          slot[row.month - 1] = {
            ca: centsToInput(row.ca_ht_cents, decimalComma),
            vat: row.vat_cents === null ? "" : centsToInput(row.vat_cents, decimalComma),
          };
        }
        setCurrent(now);
        setPrevious(before);
      })
      .catch((e) => {
        if (!stop) setErr(e instanceof Error ? e.message : "Lecture impossible");
      });
    return () => {
      stop = true;
    };
  }, [company, decimalComma]);

  if (!company) return null;

  function setCell(which: "current" | "previous", index: number, key: "ca" | "vat", value: string) {
    const next = (which === "current" ? current : previous).map((cell, i) => (i === index ? { ...cell, [key]: typedDecimal(value, decimalComma) } : cell));
    if (which === "current") setCurrent(next);
    else setPrevious(next);
  }

  async function save() {
    setBusy(true);
    setErr("");
    setMsg("");
    const rows: { year: number; month: number; ca_ht_cents: number; vat_cents: number | null }[] = [];
    const sheets = [
      { year, cells: current },
      { year: prevYear, cells: previous },
    ];
    for (const sheet of sheets) {
      for (let i = 0; i < sheet.cells.length; i++) {
        const cell = sheet.cells[i];
        const ca = readCell(cell.ca, decimalComma);
        const vat = readCell(cell.vat, decimalComma);
        const label = `${MONTHS[i]} ${sheet.year}`;
        if (ca === "bad" || vat === "bad") {
          setErr(`${label} : montant illisible.`);
          setBusy(false);
          return;
        }
        if (ca === null && vat !== null) {
          setErr(`${label} : le CA HT manque.`);
          setBusy(false);
          return;
        }
        if (ca === null) continue;
        rows.push({ year: sheet.year, month: i + 1, ca_ht_cents: ca, vat_cents: vat });
      }
    }
    try {
      await facturxFetch("realized-import.php", {
        method: "POST",
        body: JSON.stringify({ company_id: company.id, rows }),
      });
      setMsg("Mois enregistrés. Un mois vide reprend les factures.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  }

  function yearBlock(title: number, cells: Cell[], which: "current" | "previous") {
    return (
      <div>
        <h3 className="fx-prior__year">{title}</h3>
        <div className="fx-prior__head">
          <span>Mois</span>
          <span>CA HT</span>
          <span>TVA</span>
        </div>
        {MONTHS.map((name, i) => (
          <div className="fx-prior__row" key={`${title}-${name}`}>
            <span>{name}</span>
            <input
              className="fx-q-ctrl is-end"
              inputMode="decimal"
              aria-label={`${name} ${title} CA HT`}
              value={cells[i].ca}
              onChange={(e) => setCell(which, i, "ca", e.target.value)}
            />
            <input
              className="fx-q-ctrl is-end"
              inputMode="decimal"
              aria-label={`${name} ${title} TVA`}
              value={cells[i].vat}
              placeholder="—"
              onChange={(e) => setCell(which, i, "vat", e.target.value)}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <section className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Chiffre d’affaires antérieur</h2>
        <p>
          Année déjà commencée. Si FacturX n’a pas les factures d’avant, indiquez le CA HT et la TVA de chaque mois.
          L’année précédente sert aux comparaisons. Un mois vide est calculé avec les factures. Un mois rempli les
          remplace : le montant saisi s’affiche tel quel, avec la mention (importé), jusqu’à ce que vous le vidiez.
        </p>
      </header>
      <div className="fx-prior__years">
        {yearBlock(year, current, "current")}
        {yearBlock(prevYear, previous, "previous")}
      </div>
      <p className="fx-co-form__hint">TVA vide = inconnue. TVA à 0 = franchise. Le pourcentage du tableau ne s’affiche que si les deux côtés viennent des factures.</p>
      <div className="fx-ie-map__acts">
        <Button disabled={busy} onClick={() => void save()}>
          {busy ? "Enregistrement…" : "Enregistrer les mois"}
        </Button>
      </div>
      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
    </section>
  );
}
